import React, { useMemo, useState } from 'react';
import { Treemap, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { Calendar } from 'primereact/calendar';
import { useSelector, useDispatch } from 'react-redux';
import { loadMedicalServicesUserAsync, selectMedicalServicesUser } from '../dashboardSlice';
import { ChartCard } from './ChartCard';
import { stateFlagColor } from '../stateColors';

// Treemap com drill-down controlado por nós mesmos (não o type="nest" do
// recharts): o breadcrumb nativo dele recalcula o zoom a partir do PRÓPRIO
// item clicado (que já é o estado atual), então clicar em "voltar" só dá
// zoom nele mesmo de novo — nunca retorna à visão geral. Aqui guardamos a
// UF selecionada em estado local e trocamos os dados entre a visão geral
// (UFs) e a de detalhe (serviços da UF), cada uma um treemap "flat" comum,
// com um botão de voltar de verdade.
const hexToRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const LIGHT_BASE = hexToRgb('#F5F5F5');

// Cada serviço usa uma variação clara→cor-cheia-da-UF conforme a
// participação dele dentro da própria UF — mesma família de cor do estado,
// só que sequencial por magnitude dentro do grupo. Piso de 0.2 evita que o
// serviço menor de uma UF fique quase branco/invisível.
const colorForShare = (value, maxValue, targetHex) => {
    const t = maxValue > 0 ? Math.max(0.2, Math.min(1, value / maxValue)) : 0.2;
    const target = hexToRgb(targetHex);
    const [r, g, b] = [0, 1, 2].map(i => lerp(LIGHT_BASE[i], target[i], t));
    return { r, g, b, hex: `rgb(${r}, ${g}, ${b})` };
};

const rgbFromHex = (hex) => {
    const [r, g, b] = hexToRgb(hex);
    return { r, g, b, hex };
};

const textColorFor = ({ r, g, b }) => {
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 150 ? '#1B1B1B' : '#FFFFFF';
};

const truncate = (text, max) => (text.length > max ? `${text.substring(0, max - 1)}…` : text);

const NEUTRAL_COLOR = { r: 158, g: 158, b: 158, hex: '#9E9E9E' };

// Filtra pelo período em que o usuário vinculou o serviço médico (não
// existe "ano letivo" nesse domínio — ver dashboardSlice.js) — sem período
// selecionado, conta o vínculo mais recente de cada usuário sem restrição
// de data (ver comentário em getUsersCountByMedicalService no backend).
// Mesmo formato usado em ModulePerformanceHeatmap.js/StudentsRankingTable.js.
const formatDateParam = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Célula custom — cada view (visão geral de UFs ou detalhe de uma UF) é um
// treemap "flat" de um nível só, então cada nó já vem com `fill` (UF) OU
// `leafColor` (serviço), nunca os dois.
const TreemapCell = (props) => {
    const { x, y, width, height, name, value, fill, leafColor } = props;
    const isGroup = Boolean(fill);
    const color = isGroup ? rgbFromHex(fill) : (leafColor || NEUTRAL_COLOR);
    const canShowLabel = Boolean(name) && width > 55 && height > 28;
    const maxChars = Math.max(4, Math.floor(width / 7));
    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                style={{ fill: color.hex, stroke: 'var(--surface-card)', strokeWidth: isGroup ? 3 : 2, cursor: isGroup ? 'pointer' : 'default' }}
            />
            {canShowLabel && (
                <text
                    x={x + width / 2}
                    y={y + height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fill: textColorFor(color), fontSize: isGroup ? 13 : 12, fontWeight: isGroup ? 600 : 400, pointerEvents: 'none' }}
                >
                    <tspan x={x + width / 2} dy="-0.4em">{truncate(name, maxChars)}</tspan>
                    <tspan x={x + width / 2} dy="1.2em">{value}</tspan>
                </text>
            )}
        </g>
    );
};

// Totalmente customizado (não `formatter`/`itemStyle`/`labelStyle` do
// Tooltip padrão) — o conteúdo padrão do recharts usa cor de texto própria
// por linha que não segue `var(--text-color)`, ficando ilegível no tema
// escuro. Também mostra mais contexto: UF de cada serviço, % que ele
// representa dentro da própria UF, e nº de serviços quando é uma UF.
const TreemapTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const node = payload[0].payload;
    const isGroup = Boolean(node.fill);
    return (
        <div
            style={{
                backgroundColor: 'var(--surface-card)',
                border: '1px solid var(--surface-border)',
                color: 'var(--text-color)',
                borderRadius: 6,
                padding: '0.5rem 0.75rem',
                fontSize: 13,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{node.name}</div>
            {isGroup ? (
                <>
                    <div>{node.serviceCount} serviço{node.serviceCount === 1 ? '' : 's'}</div>
                    <div>{node.value} usuário{node.value === 1 ? '' : 's'} no total</div>
                    <div style={{ color: 'var(--text-color-secondary)' }}>{node.sharePercent}% de todos os usuários</div>
                </>
            ) : (
                <>
                    {node.uf && <div>UF: {node.uf}</div>}
                    <div>{node.value} usuário{node.value === 1 ? '' : 's'}</div>
                    <div style={{ color: 'var(--text-color-secondary)' }}>{node.sharePercent}% dos usuários da UF</div>
                </>
            )}
        </div>
    );
};

export const MedicalServicesUserChart = () => {
    const dispatch = useDispatch();
    const { data, status, error } = useSelector(selectMedicalServicesUser);
    const [selectedUf, setSelectedUf] = useState(null);
    // Sem useEffect de montagem aqui de propósito — a carga inicial já vem
    // do TAB_LOADERS da aba Serviços e Regiões; disparar de novo ao montar
    // duplicaria a mesma requisição. Só busca de novo quando o toggle muda.
    const [activeOnly, setActiveOnly] = useState(true);
    // value=[null, null] (em vez de null de verdade) faz o Calendar em modo
    // range achar que já tem uma data inicial selecionada — mesma ressalva
    // de ModulePerformanceHeatmap.js/StudentsRankingTable.js.
    const [dateRange, setDateRange] = useState(null);

    const onToggleActiveOnly = (e) => {
        setActiveOnly(e.value);
        const [start, end] = dateRange || [];
        dispatch(loadMedicalServicesUserAsync({ activeOnly: e.value, from: formatDateParam(start), to: formatDateParam(end) }));
    };

    // Só dispara a busca quando as duas datas estão preenchidas, ou quando
    // o usuário limpa (mesmo padrão do heatmap de Desempenho).
    const onDateRangeChange = (e) => {
        const value = e.value ?? null;
        setDateRange(value);
        const [start, end] = value || [];
        if ((start && end) || (!start && !end)) {
            dispatch(loadMedicalServicesUserAsync({ activeOnly, from: formatDateParam(start), to: formatDateParam(end) }));
        }
    };

    // Agrupado por UF uma vez só — a visão geral usa o resumo de cada
    // grupo, a visão de detalhe usa os `children` do grupo selecionado.
    const groups = useMemo(() => {
        const items = (Array.isArray(data) ? data : []).filter(item => item.userCount > 0);
        const byState = new Map();
        items.forEach(item => {
            const uf = item.state || 'N/A';
            if (!byState.has(uf)) byState.set(uf, []);
            byState.get(uf).push(item);
        });
        const grandTotal = items.reduce((sum, item) => sum + item.userCount, 0);

        return Array.from(byState.entries())
            .map(([uf, services]) => {
                const ufColor = stateFlagColor(uf);
                const maxInGroup = Math.max(...services.map(s => s.userCount));
                const stateTotal = services.reduce((sum, s) => sum + s.userCount, 0);
                const children = [...services]
                    .sort((a, b) => b.userCount - a.userCount)
                    .map(s => ({
                        name: s.medicalServiceName,
                        value: s.userCount,
                        leafColor: colorForShare(s.userCount, maxInGroup, ufColor),
                        uf,
                        sharePercent: stateTotal > 0 ? Math.round((s.userCount / stateTotal) * 1000) / 10 : 0,
                    }));
                return {
                    name: uf,
                    value: stateTotal,
                    fill: ufColor,
                    children,
                    serviceCount: children.length,
                    sharePercent: grandTotal > 0 ? Math.round((stateTotal / grandTotal) * 1000) / 10 : 0,
                };
            })
            .sort((a, b) => b.value - a.value);
    }, [data]);

    const selectedGroup = selectedUf ? groups.find(g => g.name === selectedUf) : null;
    // Sem `type="nest"`, o recharts renderiza QUALQUER `children` presente
    // de forma recursiva (aninhada), mesmo em modo "flat" — precisa tirar
    // os `children` dos itens de UF na visão geral, senão os serviços de
    // cada UF apareceriam encaixados dentro do retângulo dela mesmo sem
    // ter dado nenhum clique ainda.
    const treemapData = selectedGroup
        ? selectedGroup.children
        : groups.map(({ children, ...group }) => group);

    // Só entra em uma UF a partir da visão geral (nunca a partir da visão
    // de detalhe) — `node.fill` só existe nos itens de UF, nunca nos de
    // serviço (que usam `leafColor`). Se a UF selecionada sumir dos dados
    // num reload, `selectedGroup` vira undefined e volta pra visão geral
    // sozinho, sem precisar de nenhum reset explícito.
    const onTreemapClick = (node) => {
        if (!selectedGroup && node?.fill) {
            setSelectedUf(node.name);
        }
    };

    // Soma do que está sendo exibido: visão geral = todos os usuários de
    // todas as UFs; visão de detalhe (UF selecionada) = só os daquela UF.
    const displayTotal = treemapData.reduce((sum, item) => sum + item.value, 0);

    return (
        <ChartCard
            title="Usuários por serviço médico"
            loading={status === 'loading'}
            error={error}
            headerExtra={(
                <div className="flex align-items-center gap-2">
                    {selectedGroup && (
                        <Button
                            icon="pi pi-arrow-left"
                            label={`Voltar (${selectedGroup.name})`}
                            text
                            size="small"
                            onClick={() => setSelectedUf(null)}
                        />
                    )}
                    <Calendar
                        inputId="medical-services-date-range"
                        value={dateRange}
                        onChange={onDateRangeChange}
                        selectionMode="range"
                        dateFormat="dd/mm/yy"
                        placeholder="Todo o período"
                        showButtonBar
                        disabled={status === 'loading'}
                        className="w-full md:w-20rem"
                    />
                    <InputSwitch
                        inputId="medical-services-active-only"
                        checked={activeOnly}
                        onChange={onToggleActiveOnly}
                        disabled={status === 'loading'}
                        tooltip="Somente ativos"
                        tooltipOptions={{ position: 'top' }}
                        className="mr-2"
                    />
                </div>
            )}
            onReload={() => {
                const [start, end] = dateRange || [];
                dispatch(loadMedicalServicesUserAsync({ activeOnly, from: formatDateParam(start), to: formatDateParam(end) }));
            }}
        >
            <div>
                <ResponsiveContainer width="100%" height={420}>
                    <Treemap
                        data={treemapData}
                        dataKey="value"
                        nameKey="name"
                        aspectRatio={4 / 3}
                        isAnimationActive={false}
                        content={<TreemapCell />}
                        onClick={onTreemapClick}
                    >
                        <Tooltip content={<TreemapTooltip />} />
                    </Treemap>
                </ResponsiveContainer>
                <div className="text-center text-color-secondary text-sm mt-2">
                    Total{selectedGroup ? ` em ${selectedGroup.name}` : ' geral'}: <strong>{displayTotal}</strong> usuário{displayTotal === 1 ? '' : 's'}
                </div>
            </div>
        </ChartCard>
    );
};
